import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogActions,
    DialogTitle,
    Button,
    Tabs,
    Tab,
    Chip,
    Badge,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {
    GridColDef,
    GridCellParams,
    GridRenderCellParams,
    GridRenderEditCellParams,
} from "@mui/x-data-grid-premium";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AttachFile from "@mui/icons-material/AttachFile";
import moment from "moment";

import { MainLayout } from "../../layout/mainLayout";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { MuiEditableDataGrid } from "../../framework/editableDataGrid/muiEditableDataGrid";
import { MaintenanceAttachmentsPanel } from "./maintenanceAttachmentsPanel";

import {
    fetchVehicleMaintenanceData,
    fetchVehicleOptions,
    deleteVehicleMaintenanceData,
    saveVehicleMaintenance,
    approveVehicleMaintenanceReminderData,
} from "../../../store/vehicleSlice";
import { fetchAllVehicleGroups } from "../../../store/vehicleSlice";
import { fetchGroupedCostCenterOptions } from "../../../store/costCenterSlice";
import { VehicleMaintenanceDto } from "../../../models/vehicle/vehicleMaintenance";
import { FuelType } from "../../../models/vehicle/fuelType";
import { FieldError } from "../../framework/editableDataGrid/muiEditableDataGrid";
import { showSuccessMessage } from "../../../models/store/storeActions";
import { stringOrNumberSortComparator } from "../../framework/dataGrid/sortComparator";
import { Base } from "../../../framework/base";
import { MuiDataGridDateTimePicker } from "../../framework/editableDataGrid/muiDataGridDateTimePicker";
import { MaintenanceReminderDialog } from "../maintenanceReminderView/MaintenanceReminderDialog";
import { AddAlarm, Alarm } from "@mui/icons-material";
import { MaintenanceReminderStatus } from "../../../models/vehicle/vehicleMaintenanceReminder";
import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import Grid2 from "@mui/material/Unstable_Grid2";

export interface VehicleMaintenanceDetailsListItem extends VehicleMaintenanceDto {
    vehicleGroupName: string;
    costCenterName: string;
    fuelTypeName: string;
}

export const MaintenanceView = () => (
    <MainLayout>
        <MaintenanceViewContent />
    </MainLayout>
);

export const MaintenanceViewContent = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const {
        vehicles,
        vehicleMaintenances,
        allVehicleGroups,
        vehicleMaintenancesLoading,
    } = useAppSelector((s) => s.vehicle);

    const [reminderDialogOpen, setReminderDialogOpen] =
        useState<boolean>(false);
    const [editId, setEditId] = useState<string | undefined>();
    const costCenters = useAppSelector((s) => s.costCenter.groupedOptions);

    const [fromDate] = useState<Date>(moment().startOf("year").toDate());
    const [untilDate] = useState<Date | null>(null);

    const [rows, setRows] = useState<VehicleMaintenanceDetailsListItem[]>([]);
    const [filteredRows, setFilteredRows] = useState<
        VehicleMaintenanceDetailsListItem[]
    >([]);
    const [deleteRow, setDeleteRow] =
        useState<VehicleMaintenanceDetailsListItem | null>(null);
    const [approveRow, setApproveRow] =
        useState<VehicleMaintenanceDetailsListItem | null>(null);

    const [highlightedRowId, setHighlightedRowId] = useState<string | null>(
        null
    );
    const [expandedRow, setExpandedRow] = useState<{
        id: string;
        field: string;
    }>();
    const [selectedVehicleMaintenance, setSelectedVehicleMaintenance] =
        useState<VehicleMaintenanceDetailsListItem>(null);

    const [currentTab, setCurrentTab] = useState<MaintenanceReminderStatus>(
        MaintenanceReminderStatus.Incoming
    );

    const [awaitingApprovalCount, setAwaitingApprovalCount] =
        useState<number>(0);
    const [exceededCount, setExceededCount] = useState<number>(0);
    const [incomingCount, setIncomingCount] = useState<number>(0);
    const [completedCount, setCompletedCount] = useState<number>(0);

    /* Helper methods */
    const getVehicleGroupName = (id?: string) =>
        allVehicleGroups?.find((g) => g.id === id)?.name ?? "";

    const getCostCenterName = (id?: string) =>
        costCenters?.find((c) => c.value === id)?.label ?? "";

    const getFuelTypeName = (fuelType?: FuelType) =>
        fuelType ? t(`common.fuelTypes.fuelType.${FuelType[fuelType]}`) : "";

    const openDialog = (row: VehicleMaintenanceDetailsListItem) => {
        setSelectedVehicleMaintenance(row);
        setReminderDialogOpen(true);
    };

    /* Load data */
    useEffect(() => {
        fetchVehicleMaintenances();

        if (!vehicles?.length) {
            dispatch(fetchVehicleOptions() as any);
        }
        if (!allVehicleGroups?.length) {
            dispatch(fetchAllVehicleGroups([]) as any);
        }
        if (!costCenters?.length) {
            dispatch(fetchGroupedCostCenterOptions() as any);
        }
    }, []);

    const fetchVehicleMaintenances = () => {
        dispatch(
            fetchVehicleMaintenanceData({
                fromDate: fromDate,
                untilDate: untilDate,
                vehicleIds: null,
            }) as any
        );
    };

    /* Map row data from vehicles and state data */
    const visibleMaintenanceRowData = useMemo(() => {
        const mergedData = (vehicleMaintenances ?? []).map((m) => {
            const v = vehicles?.find((x) => x.id === m.vehicleId);

            return {
                ...m,
                vehicleId: v?.id,
                registerNumber: v?.registerNumber,
                brand: v?.brand,
                number: v?.number,
                fuelTypeName: getFuelTypeName(v?.fuelType),
                vehicleGroupName: getVehicleGroupName(v?.vehicleGroupId),
                costCenterName: getCostCenterName(v?.costCenterId),
            } as VehicleMaintenanceDetailsListItem;
        });
        return mergedData;
    }, [vehicleMaintenances, vehicles]);

    useEffect(() => {
        if (!vehicleMaintenancesLoading) {
            setRows(() => [...visibleMaintenanceRowData]);
        }
    }, [visibleMaintenanceRowData, vehicleMaintenancesLoading]);

    useEffect(() => {
        filterRowsByCurrentTab(currentTab);

        setIncomingCount(
            rows.filter(
                (row) =>
                    row.vehicleMaintenanceReminder?.status ===
                        MaintenanceReminderStatus.Approaching ||
                    row.vehicleMaintenanceReminder?.status ===
                        MaintenanceReminderStatus.Incoming
            ).length
        );
        setExceededCount(
            rows.filter(
                (row) =>
                    row.vehicleMaintenanceReminder?.status ===
                    MaintenanceReminderStatus.Exceeded
            ).length
        );
        setAwaitingApprovalCount(
            rows.filter(
                (row) =>
                    row.vehicleMaintenanceReminder?.status ===
                    MaintenanceReminderStatus.AwaitingApproval
            ).length
        );
        setCompletedCount(
            rows.filter(
                (row) =>
                    row.vehicleMaintenanceReminder?.status ===
                    MaintenanceReminderStatus.Completed
            ).length
        );
    }, [rows]);

    const isCellEditable = (params: GridCellParams) => {
        const maintenanceFields = [
            "title",
            "maintenanceDate",
            "maintenanceOdometerReading",
            "price",
            "comment",
            "driverComment",
        ];

        // Vehicle can be selected ONLY for new rows
        if (params.field === "vehicleId") {
            return params.id === "new";
        }

        return maintenanceFields.includes(params.field);
    };

    /* Column definitions */
    const columns: GridColDef<VehicleMaintenanceDetailsListItem>[] = useMemo(
        () => [
            {
                field: "status",
                headerName: t("common.status"),
                editable: false,
                flex: 1.5,
                renderCell: (
                    params: GridRenderCellParams<VehicleMaintenanceDto>
                ) =>
                    getStatusText(
                        params.row?.vehicleMaintenanceReminder?.status
                    ),
            },
            {
                field: "vehicleId",
                headerName: t("vehicle.vehicle"),
                flex: 1,
                editable: true,
                type: "singleSelect",
                valueOptions: vehicles?.map((v) => ({
                    value: v.id,
                    label: `${v.registerNumber}`,
                })),
            },
            {
                field: "brand",
                headerName: t("vehicle.brand"),
                flex: 1,
                editable: false,
            },
            {
                field: "title",
                headerName: t("vehicleMaintenance.title"),
                editable: true,
                flex: 1.5,
            },
            {
                field: "maintenanceDate",
                headerName: t("vehicleMaintenance.maintenanceDate"),
                editable: true,
                flex: 1.5,
                renderCell: (
                    params: GridRenderCellParams<VehicleMaintenanceDto>
                ) => getDate(params.row?.maintenanceDate),
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <MuiDataGridDateTimePicker {...params} dateOnly={true} />
                ),
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "maintenanceOdometerReading",
                headerName: t("vehicleMaintenance.maintenanceOdometerReading"),
                editable: true,
                type: "number",
                flex: 1,
            },
            {
                field: "price",
                headerName: t("vehicleMaintenance.price"),
                editable: true,
                type: "number",
                flex: 1,
            },
            {
                field: "comment",
                headerName: t("vehicleMaintenance.comment"),
                editable: true,
                flex: 2,
            },
            {
                field: "driverComment",
                headerName: t("vehicleMaintenance.driverComment"),
                editable: true,
                flex: 2,
            },
            {
                field: "actions",
                type: "actions",
                width: 150,
                renderCell: (
                    params: GridRenderCellParams<VehicleMaintenanceDetailsListItem>
                ) => (
                    <Grid2 sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end"
                    }}
                    >
                        {params.row.vehicleMaintenanceReminder?.status ===
                        MaintenanceReminderStatus.AwaitingApproval && (
                            <Tooltip
                                title={t("vehicleMaintenanceReminder.approveMaintenance")}
                            >
                                <IconButton
                                    disableRipple
                                    sx={{ p: 0.5 }}
                                    onClick={() => setApproveRow(params.row)}
                                >
                                    <CheckCircleIcon color="info" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {params.id !== "new" && (
                            <IconButton
                                disableRipple
                                sx={{ p: 0.5 }}
                                onClick={() =>
                                    setExpandedRow((prev) =>
                                        prev?.id === params.id.toString()
                                            ? undefined
                                            : {
                                                id: params.row.id,
                                                field: "attachments",
                                            }
                                    )
                                }
                            >
                                {params.row.attachments?.length ? (
                                    <Badge
                                        overlap="circular"
                                        badgeContent={params.row.attachments.length}
                                        color="primary"
                                    >
                                        <AttachFile color="primary" />
                                    </Badge>
                                ) : (
                                    <AttachFile color="disabled" />
                                )}
                            </IconButton>
                        )}
                        <IconButton
                            disableRipple
                            sx={{ p: 0.5 }}
                            onClick={() => openDialog(params.row)}
                        >
                            {params.row.vehicleMaintenanceReminder ? (
                                <Alarm color="success" />
                            ) : (
                                <AddAlarm color="info" />
                            )}
                        </IconButton>
                        <IconButton
                            disableRipple
                            sx={{ p: 0.5 }}
                            onClick={() => setDeleteRow(params.row)}
                        >
                            <DeleteIcon color="error" />
                        </IconButton>
                    </Grid2>
                )
            },
        ],
        [vehicles]
    );

    /* Grid event handlers */
    const handleAddRow = (): Partial<VehicleMaintenanceDetailsListItem> => {
        return {
            id: "new",
            vehicleId: "",
            title: "",
            maintenanceDate: null,
            maintenanceOdometerReading: null,
            price: null,
            comment: "",
            driverComment: "",
        };
    };

    const validateRow = (
        newRowData: VehicleMaintenanceDetailsListItem
    ): FieldError[] => {
        const errors: FieldError[] = [];

        if (!newRowData.vehicleId) {
            errors.push({
                field: "vehicleId",
                error: t("validation.fieldIsRequired"),
            });
        }

        return errors;
    };

    const handleSubmit = async(
        values: VehicleMaintenanceDto
    ): Promise<VehicleMaintenanceDto> => {
        return new Promise((resolve, reject) => {
            const isNew = values?.id === null;

            // set date to null if value is empty, otherwise format as YYYY-MM-DD
            if (
                values.maintenanceDate === "" ||
                values.maintenanceDate === null
            ) {
                values.maintenanceDate = null;
            } else if (values.maintenanceDate) {
                // If it's a Date object or datetime string, format it as YYYY-MM-DD for DateOnly
                const date = new Date(values.maintenanceDate);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                values.maintenanceDate = `${year}-${month}-${day}`;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            dispatch(
                saveVehicleMaintenance({ vehicleMaintenanceDto: values }) as any
            )
                .then((action: { payload: VehicleMaintenanceDto }) => {
                    setHighlightedRowId(action.payload?.id);
                    setTimeout(() => setHighlightedRowId(null), 2000);

                    const message = isNew
                        ? t("vehicleMaintenance.maintenanceAddedSuccessfully")
                        : t("vehicleMaintenance.maintenanceEditedSuccessfully");
                    dispatch(showSuccessMessage(message));
                    resolve(action.payload);
                })
                .catch((error) => {
                    const message = isNew
                        ? t("vehicleMaintenance.saveFailed")
                        : t("vehicleMaintenance.editFailed");
                    console.error("Error saving vehicle maintenance: ", error);
                    reject(new Error(message));
                });
        });
    };

    const handleTabChange = (
        event: React.SyntheticEvent,
        newValue: MaintenanceReminderStatus
    ) => {
        setCurrentTab(newValue);
        filterRowsByCurrentTab(newValue);
    };

    const filterRowsByCurrentTab = (tab: MaintenanceReminderStatus) => {
        if (tab === MaintenanceReminderStatus.Incoming) {
            setFilteredRows(
                rows.filter(
                    (row) =>
                        row.vehicleMaintenanceReminder?.status ===
                            MaintenanceReminderStatus.Approaching ||
                        row.vehicleMaintenanceReminder?.status ===
                            MaintenanceReminderStatus.Incoming
                )
            );
        } else if (tab === MaintenanceReminderStatus.Exceeded) {
            setFilteredRows(
                rows.filter(
                    (row) =>
                        row.vehicleMaintenanceReminder?.status ===
                        MaintenanceReminderStatus.Exceeded
                )
            );
        } else if (tab === MaintenanceReminderStatus.AwaitingApproval) {
            setFilteredRows(
                rows.filter(
                    (row) =>
                        row.vehicleMaintenanceReminder?.status ===
                        MaintenanceReminderStatus.AwaitingApproval
                )
            );
        } else if (tab === MaintenanceReminderStatus.Completed) {
            setFilteredRows(
                rows.filter(
                    (row) =>
                        row.vehicleMaintenanceReminder?.status ===
                        MaintenanceReminderStatus.Completed
                )
            );
        } else {
            setFilteredRows(rows);
        }
    };

    const getDetailsComponent = useCallback(
        (params) => (
            <MaintenanceAttachmentsPanel
                maintenanceId={params.row.id}
                onClose={() => setExpandedRow(undefined)}
            />
        ),
        []
    );

    const getStatusText = (status?: MaintenanceReminderStatus) =>
        status
            ? t(
                `vehicleMaintenanceReminder.status.${MaintenanceReminderStatus[status]}`
            )
            : "-";

    /* UI rendering */
    return (
        <>
            {!vehicleMaintenancesLoading && (
                <>
                    <Stack direction="row" spacing={2}>
                        <Badge
                            badgeContent={exceededCount}
                            color="primary"
                            showZero
                        >
                            <Chip
                                label={t(
                                    "vehicleMaintenanceReminder.status.Exceeded"
                                )}
                                color="error"
                                variant="outlined"
                            />
                        </Badge>
                        <Badge
                            badgeContent={incomingCount}
                            color="primary"
                            showZero
                        >
                            <Chip
                                label={t(
                                    "vehicleMaintenanceReminder.status.Incoming"
                                )}
                                color="orange"
                                variant="outlined"
                            />
                        </Badge>
                        <Badge
                            badgeContent={awaitingApprovalCount}
                            color="primary"
                            showZero
                        >
                            <Chip
                                label={t(
                                    "vehicleMaintenanceReminder.status.AwaitingApproval"
                                )}
                                color="primary"
                                variant="outlined"
                            />
                        </Badge>
                        <Badge
                            badgeContent={completedCount}
                            color="primary"
                            showZero
                        >
                            <Chip
                                label={t(
                                    "vehicleMaintenanceReminder.status.Completed"
                                )}
                                color="success"
                                variant="outlined"
                            />
                        </Badge>
                    </Stack>
                    <Tabs
                        variant="fullWidth"
                        onChange={handleTabChange}
                        value={currentTab}
                        sx={{ marginBottom: 2 }}
                    >
                        <Tab
                            label={t(
                                "vehicleMaintenanceReminder.status.Incoming"
                            )}
                            value={MaintenanceReminderStatus.Incoming}
                        />
                        <Tab
                            label={t(
                                "vehicleMaintenanceReminder.status.Exceeded"
                            )}
                            value={MaintenanceReminderStatus.Exceeded}
                        />
                        <Tab
                            label={t(
                                "vehicleMaintenanceReminder.status.AwaitingApproval"
                            )}
                            value={MaintenanceReminderStatus.AwaitingApproval}
                        />
                        <Tab
                            label={t(
                                "vehicleMaintenanceReminder.status.Completed"
                            )}
                            value={MaintenanceReminderStatus.Completed}
                        />
                        <Tab label={t("common.all")} value="all" />
                    </Tabs>
                </>
            )}

            <MuiEditableDataGrid<Partial<VehicleMaintenanceDetailsListItem>>
                title={t("vehicleMaintenance.listTitle")}
                editId={editId}
                setEditId={setEditId}
                persistStateKey="EditableMaintenanceTableState_V3"
                columns={columns}
                rows={filteredRows}
                setRows={setFilteredRows}
                handleAddRow={handleAddRow}
                handleSubmit={handleSubmit}
                validateRow={validateRow}
                addRowText={t("vehicleMaintenance.newMaintenance")}
                isCellEditable={isCellEditable}
                pagination
                highlightedRowId={highlightedRowId}
                disableColumnSelector={true}
                detailsComponent={getDetailsComponent}
                detailPanelExpandedRowIds={[expandedRow?.id].filter(Boolean)}
            />

            <Dialog open={!!deleteRow} onClose={() => setDeleteRow(null)}>
                <DialogTitle>
                    {t("vehicleMaintenance.confirmDeleteForVehicle", {
                        vehicle:
                            vehicles?.find(
                                (v) => v.id === deleteRow?.vehicleId
                            )?.registerNumber ?? ""
                    })}
                </DialogTitle>
                <DialogActions>
                    <Button onClick={() => setDeleteRow(null)}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        color="error"
                        disabled={!deleteRow?.id}
                        onClick={() => {
                            dispatch(
                                deleteVehicleMaintenanceData({
                                    vehicleMaintenanceId: deleteRow?.id,
                                }) as any
                            );
                            dispatch(
                                showSuccessMessage(
                                    t(
                                        "vehicleMaintenance.maintenanceDeletedSuccessfully"
                                    )
                                )
                            );
                            setRows((r) =>
                                r.filter((x) => x.id !== deleteRow?.id)
                            );
                            setDeleteRow(null);
                        }}
                    >
                        {t("common.remove")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!approveRow} onClose={() => setApproveRow(null)}>
                <DialogTitle>
                    {t("vehicleMaintenanceReminder.confirmApproveForVehicle", {
                        vehicle:
                            vehicles?.find(
                                (v) => v.id === approveRow?.vehicleId
                            )?.registerNumber ?? ""
                    })}
                </DialogTitle>
                <DialogActions>
                    <Button onClick={() => setApproveRow(null)}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        color="success"
                        disabled={!approveRow?.id}
                        onClick={() => {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                            dispatch(
                                approveVehicleMaintenanceReminderData({
                                    vehicleMaintenanceId: approveRow?.id,
                                }) as any
                            ).then(() => {
                                dispatch(
                                    showSuccessMessage(
                                        t(
                                            "vehicleMaintenanceReminder.approveSuccess"
                                        )
                                    )
                                );
                                fetchVehicleMaintenances();
                                setApproveRow(null);
                            });
                        }}
                    >
                        {t("vehicleMaintenanceReminder.approve")}
                    </Button>
                </DialogActions>
            </Dialog>

            <MaintenanceReminderDialog
                isEdit={
                    !!selectedVehicleMaintenance?.vehicleMaintenanceReminder
                }
                open={reminderDialogOpen}
                maintenanceItem={selectedVehicleMaintenance}
                maintenanceReminderItem={
                    selectedVehicleMaintenance?.vehicleMaintenanceReminder
                }
                onClose={() => setReminderDialogOpen(false)}
                fetchVehicleMaintenances={fetchVehicleMaintenances}
            />
        </>
    );
};

export const getDate = (date: string) => {
    if (!date) return "-";
    return Base.dayjsToDateStr(date);
};

export const getDateTime = (date: string) => {
    if (!date) return null;
    return Base.dayjsDateToDateTimeStr(date);
};
